import {
  getTaskTypeFieldName,
  TaskType,
  TaskTypeAttributeName,
} from "@/data/models/Task";
import { TimelineVisibleArea } from "@/data/models/TimelineSettings";
import { HourAndMinute } from "@/data/models/HourAndMinute";
import { DateTime } from "luxon";
import {
  COLOR_SCHEDULING_TASK_DAMAGE_ASSESSMENT,
  COLOR_SCHEDULING_TASK_DRYING_CONCLUSION,
  COLOR_SCHEDULING_TASK_DRYING_SETUP,
  COLOR_SCHEDULING_TASK_INTERMEDIATE_MEASUREMENT,
  COLOR_SCHEDULING_TASK_LEAK_DETECTION,
  COLOR_SCHEDULING_TASK_REPAIR,
} from "@/utils/colors";

export interface PlanningBoardSettings {
  visibleArea: TimelineVisibleArea;
  visibleAreaOfDayStart: HourAndMinute;
  visibleAreaOfDayEnd: HourAndMinute;
  visibleAreaOfDayBufferInMinutes: number;
  taskColors: TaskColors;
}

export const ObjectiveFunctionWeightNames = [
  "total",

  "travelTime",
  "timeGap",

  "targetDate",
  "targetDateFixed",

  "targetTime",
  "targetTimeFixed",

  "targetUser",
  "targetQualification",

  "dailyWorkTimeExceedance",
  "weeklyWorkTimeExceedance",
] as const;

export type ObjectiveFunctionWeightAttribute = typeof ObjectiveFunctionWeightNames[number];
export type ObjectiveFunctionWeights = {
  [Attribute in ObjectiveFunctionWeightAttribute]: number;
};

export interface ObjectiveFunctionParameters {
  weights: ObjectiveFunctionWeights;
}

export interface WorkingHoursSettings {
  defaultWorkDayStart: HourAndMinute;
  defaultWorkDayEnd: HourAndMinute;

  earliestWorkDayStart: HourAndMinute;
  latestWorkDayEnd: HourAndMinute;

  defaultWorkingHoursPerDay: number;
  maxWorkingHoursPerDay: number;

  defaultWorkingHoursPerWeek: number;
  maxWorkingHoursPerWeek: number;

  lunchBreakAfter: HourAndMinute;
  lunchBreakDurationInMin: number;

  shortBreakThresholdHours?: number;
  longBreakThresholdHours?: number;
  longBreakDurationInMin?: number;
  minBreakDurationInMin?: number;
}

export function getEarliestLunchBreakForDate(date: Date, workingHoursSettings: WorkingHoursSettings) {
  return DateTime.fromJSDate(date).set({
    hour: workingHoursSettings.lunchBreakAfter.hour,
    minute: workingHoursSettings.lunchBreakAfter.minute,
    second: 0,
    millisecond: 0,
  })
}

export type TaskColorAttribute = typeof TaskTypeAttributeName[number];

export type TaskColors = {
  [Attribute in TaskColorAttribute]: string;
};

export type DefaultTaskDurations = {
  [Attribute in TaskColorAttribute]: number;
};

export interface TravelTimeSettings {
  defaultTravelTimeBetweenTaskAndDepotInMin: number;
  defaultTravelTimeBetweenTasksInMin: number;
  travelTimeBufferInMin: number;
  doTravelTimeCalculationViaMap: boolean;
}

export interface DefaultBreakDurations {
  lunchBreak: number;
  maxTimeWithoutBreak: number;
}

export enum OccupiedTimeUsage {
  NONE = "NONE",
  SHOW = "SHOW",
  PLAN = "PLAN",
}

export interface PlanningControlSettings {
  breakUsage: OccupiedTimeUsage;
  travelTimeUsage: OccupiedTimeUsage;
  defaultTaskDurations: DefaultTaskDurations;
  objectiveFunctionParameters: ObjectiveFunctionParameters;
  acceptableDelayInMin: number;
}

export interface SchedulingSettings {
  stepSizeInMin: number;
  planningBoardSettings: PlanningBoardSettings;

  workingHoursSettings: WorkingHoursSettings;
  planningControlSettings: PlanningControlSettings;

  travelTimeSettings: TravelTimeSettings;
}

export function getNextDateTimeWithMatchingStepSize(
  dateTime: DateTime,
  stepSizeInMinutes: number
) {
  const minute = dateTime.minute;
  const roundedToStepSize =
    Math.ceil(dateTime.minute / stepSizeInMinutes) * stepSizeInMinutes;

  return dateTime.plus({ minute: roundedToStepSize - minute });
}

export function getTaskTypeColor(
  taskType: TaskType,
  planningBoardSettings: PlanningBoardSettings
): string {
  const fieldName = getTaskTypeFieldName(taskType);
  return planningBoardSettings.taskColors[fieldName];
}

export function getDefaultTaskDurationInMinutes(
  taskType: TaskType,
  schedulingSettings: SchedulingSettings
): number {
  const fieldName = getTaskTypeFieldName(taskType);
  return schedulingSettings.planningControlSettings.defaultTaskDurations[
    fieldName
  ];
}

export function createDefaultSchedulingSettings(): SchedulingSettings {
  return {
    stepSizeInMin: 15,
    planningControlSettings: {
      breakUsage: OccupiedTimeUsage.PLAN,
      travelTimeUsage: OccupiedTimeUsage.PLAN,
      acceptableDelayInMin: 120,

      defaultTaskDurations: {
        leakDetection: 180,
        damageAssessment: 120,
        dryingSetup: 180,
        intermediateMeasurement: 60,
        dryingConclusion: 120,
        repair: 240,
      },

      objectiveFunctionParameters: {
        weights: {
          total: 100,

          travelTime: 100,
          timeGap: -30,

          targetDate: -10,
          targetDateFixed: -20,

          targetTime: -10,
          targetTimeFixed: -20,

          targetUser: -20,
          targetQualification: -10,

          dailyWorkTimeExceedance: -50,
          weeklyWorkTimeExceedance: -50,
        },
      },
    },
    planningBoardSettings: {
      visibleArea: TimelineVisibleArea.TWO_WEEKS,
      visibleAreaOfDayBufferInMinutes: 30,
      visibleAreaOfDayStart: {
        hour: 7,
        minute: 30,
      },
      visibleAreaOfDayEnd: {
        hour: 18,
        minute: 30,
      },
      taskColors: {
        leakDetection: COLOR_SCHEDULING_TASK_LEAK_DETECTION,
        damageAssessment: COLOR_SCHEDULING_TASK_DAMAGE_ASSESSMENT,
        dryingSetup: COLOR_SCHEDULING_TASK_DRYING_SETUP,
        intermediateMeasurement: COLOR_SCHEDULING_TASK_INTERMEDIATE_MEASUREMENT,
        dryingConclusion: COLOR_SCHEDULING_TASK_DRYING_CONCLUSION,
        repair: COLOR_SCHEDULING_TASK_REPAIR,
      },
    },

    travelTimeSettings: {
      defaultTravelTimeBetweenTaskAndDepotInMin: 45,
      defaultTravelTimeBetweenTasksInMin: 60,
      doTravelTimeCalculationViaMap: false,
      travelTimeBufferInMin: 10,
    },

    workingHoursSettings: {
      lunchBreakDurationInMin: 60,
      lunchBreakAfter: {
        hour: 12,
        minute: 0,
      },
      shortBreakThresholdHours: 6,
      longBreakThresholdHours: 9,
      longBreakDurationInMin: 45,
      minBreakDurationInMin: 15,
      defaultWorkDayStart: {
        hour: 8,
        minute: 0,
      },

      defaultWorkDayEnd: {
        hour: 18,
        minute: 0,
      },

      earliestWorkDayStart: {
        hour: 7,
        minute: 30,
      },

      latestWorkDayEnd: {
        hour: 19,
        minute: 0,
      },

      defaultWorkingHoursPerDay: 8,
      maxWorkingHoursPerDay: 10,

      defaultWorkingHoursPerWeek: 40,
      maxWorkingHoursPerWeek: 45,
    },
  };
}

export function calculateWorkDayStart(
  dateTime: DateTime,
  startOfWorkDay: HourAndMinute,
  travelTimeFromDepotInMin: number,
  stepSizeInMin: number
) {
  const workDayStart = dateTime
    .set({
      hour: startOfWorkDay.hour,
      minute: startOfWorkDay.minute,
      second: 0,
      millisecond: 0,
    })
    .plus({ minutes: travelTimeFromDepotInMin });

  return getNextDateTimeWithMatchingStepSize(workDayStart, stepSizeInMin);
}
